import React, { useState, useRef, useCallback } from "react";
import { AutoComplete, Form, Input, Select, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useAxiosClient from "../../axios-client";
import { toast } from "react-toastify";
import { SHELL_THEME as THEME, STATUS } from "../../shellTokens";
import { useStateContext } from "../../contexts/ContextProvider";
import { Band, Mono, SectionCard } from "./orderFormShared";

const { Option } = Select;

/**
 * Customer card — channel, lookup, then address, as three labelled bands.
 *
 * Which channel an order belongs to *is* a fact about its customer, so it lives
 * here rather than in a card of its own. The lookup has four real states —
 * idle, searching, matched, new — because "did this find someone?" is the one
 * question an operator asks of this card, and a silent autofill never answers it.
 */

const CHANNELS = [
  { title: "Online", value: "online" },
  { title: "Offline", value: "offline" },
];

const ChannelToggle = ({ channel, locked, onChange }) => (
  <div
    style={{
      display: "inline-flex",
      background: "var(--surface2)",
      border: "1px solid var(--border)",
      borderRadius: 8,
      padding: 3,
      gap: 2,
    }}
    title={locked ? "Channel can't be changed after an order is created" : undefined}
  >
    {CHANNELS.map((opt) => {
      const active = channel === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          disabled={locked}
          onClick={locked ? undefined : () => onChange(opt.value)}
          style={{
            height: 28,
            padding: "0 14px",
            borderRadius: 6,
            border: "none",
            display: "flex",
            alignItems: "center",
            font: "600 12px Outfit, sans-serif",
            fontFamily: "inherit",
            cursor: locked ? "not-allowed" : "pointer",
            background: active ? "var(--primary)" : "transparent",
            color: active ? "#fff" : "var(--muted)",
            opacity: locked && !active ? 0.6 : 1,
          }}
        >
          {opt.title}
        </button>
      );
    })}
  </div>
);

/** The strip a match promotes to: who was found, and both ways out of it. */
const MatchStrip = ({ customer, editing, onEdit, onReject }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      marginTop: 12,
      padding: "10px 12px",
      borderRadius: 9,
      background: "rgba(52,211,153,.10)",
      border: "1px solid rgba(52,211,153,.28)",
    }}
  >
    <span style={{ font: "600 13px Outfit, sans-serif", color: "var(--text)" }}>
      {customer.name}
    </span>
    <Mono style={{ fontSize: 12, color: "var(--muted)" }}>{customer.phone}</Mono>
    <span style={{ font: "400 12px Outfit, sans-serif", color: STATUS.ok }}>
      {customer.ordersCount != null
        ? `${customer.ordersCount} previous order${customer.ordersCount === 1 ? "" : "s"}`
        : "Existing customer"}
    </span>
    <span style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
      <button type="button" className="et-of-chip" onClick={onEdit} disabled={editing}>
        {editing ? "Editing details" : "Edit details"}
      </button>
      <button type="button" className="et-of-chip" onClick={onReject}>
        Not this person
      </button>
    </span>
  </div>
);

const OrderCustomerForm = (props) => {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();

  // The AutoComplete's dropdown portals to <body>, outside the element the
  // shell declares its CSS variables on, so an option's colours have to be hex.
  const t = THEME[darkMode ? "dark" : "light"];

  const {
    districts = [],
    districtLoading,
    upazilas = [],
    upazilaLoading,
    divisionLoading,
    divisions = [],
    orderForm,
    onDivisionSelect,
    onDistrictSelect,
    onCustomerFound,
    // 'online' | 'offline' — scopes the phone search to the matching channel and
    // switches the Facebook label to "Reference" for offline (ex-merchant) customers.
    channel = "online",
    // Omit onChannelChange to lock the channel (editing an existing order).
    onChannelChange,
    marketplaces = [],
  } = props;
  const isOffline = channel === "offline";
  const channelLocked = typeof onChannelChange !== "function";

  const [searching, setSearching] = useState(false);
  const [matched, setMatched] = useState(null);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [queried, setQueried] = useState(false);
  const searchTimeout = useRef(null);

  const handleSearch = useCallback(
    (query) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      // A new search invalidates any previously matched customer, so a manual
      // entry creates a fresh record instead of silently reusing an old match.
      orderForm.setFieldValue("customerId", undefined);

      if (!query || query.trim().length < 3) {
        setAutocompleteOptions([]);
        setMatched(null);
        setFieldsDisabled(false);
        setQueried(false);
        return;
      }

      searchTimeout.current = setTimeout(async () => {
        setSearching(true);
        try {
          const res = await axiosClient.get(
            `/customers/searchByPhone?phone=${encodeURIComponent(query.trim())}&type=${channel}`,
          );
          const customers = res.data;

          setAutocompleteOptions(
            customers.map((c) => ({
              value: String(c.id),
              label: (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                  <span
                    style={{
                      flex: "none",
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: t.activeBg,
                      color: t.activeText,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      font: "700 11px Outfit, sans-serif",
                    }}
                  >
                    {(c.name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.25 }}>
                    <span style={{ font: "600 13px Outfit, sans-serif", color: t.text }}>{c.name}</span>
                    <Mono style={{ fontSize: 11.5, color: t.muted }}>{c.phone}</Mono>
                  </span>
                  {c.ordersCount != null && (
                    <span style={{ marginLeft: "auto", font: "400 11px Outfit, sans-serif", color: t.dim }}>
                      {c.ordersCount} order{c.ordersCount === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              ),
              customer: c,
            })),
          );
          setQueried(true);
        } catch (error) {
          const message =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
          toast.error(message);
        } finally {
          setSearching(false);
        }
      }, 400);
    },
    [axiosClient, orderForm, channel, t],
  );

  const handleSelect = useCallback(
    (value, option) => {
      const c = option.customer;
      if (!c) return;

      setMatched(c);
      setFieldsDisabled(true);

      const divisionId = c.division?.value;
      const districtId = c.district?.value;
      const upazilaId = c.upazila?.value ?? null;

      // Trigger cascade loading for district and upazila dropdowns
      if (onDivisionSelect) onDivisionSelect(divisionId);
      if (onDistrictSelect) onDistrictSelect(districtId);

      orderForm.setFieldsValue({
        customerId: c.id,
        phone: c.phone,
        name: c.name,
        facebookId: c.facebook,
        altPhone: c.altPhone,
        address: c.address,
        division: divisionId,
        district: districtId,
        upazila: upazilaId,
      });
      if (onCustomerFound) onCustomerFound(c);
    },
    [onDivisionSelect, onDistrictSelect, orderForm, onCustomerFound],
  );

  /** Drops the match and re-opens the form as a brand new customer. */
  const rejectMatch = useCallback(() => {
    setMatched(null);
    setFieldsDisabled(false);
    setAutocompleteOptions([]);
    setQueried(false);
    orderForm.setFieldsValue({
      customerId: undefined,
      name: undefined,
      facebookId: undefined,
      altPhone: undefined,
      address: undefined,
      division: undefined,
      district: undefined,
      upazila: undefined,
    });
    if (onDivisionSelect) onDivisionSelect(null);
    if (onDistrictSelect) onDistrictSelect(null);
  }, [orderForm, onDivisionSelect, onDistrictSelect]);

  const lookupHint = searching
    ? "Searching…"
    : matched
      ? "Matched — details below are filled from the existing record."
      : queried && autocompleteOptions.length === 0
        ? "No match. Fill the fields below and a new customer is created."
        : "Type a phone number to find an existing customer, or just fill the fields below for a new one.";

  const hintColor = matched ? STATUS.ok : queried && !searching && !autocompleteOptions.length ? STATUS.warn : "var(--muted)";

  return (
    <SectionCard
      id="et-of-section-customer"
      title="Customer"
      subtitle="Channel, lookup, then address"
      bodyStyle={{ padding: 0 }}
    >
      {/* Carries the matched customer's id so the backend reuses (dedups) it. */}
      <Form.Item name="customerId" hidden>
        <Input type="hidden" />
      </Form.Item>

      <Band label="Channel">
        <div className="et-of-channel">
          <ChannelToggle channel={channel} locked={channelLocked} onChange={onChannelChange} />
          {!isOffline && (
            <div style={{ flex: 1, minWidth: 180, maxWidth: 240 }}>
              <Form.Item
                name="marketplace"
                rules={[{ required: true, message: "Please select a marketplace!" }]}
              >
                <Select placeholder="Marketplace">
                  {marketplaces.map((m) => (
                    <Option value={m.id} key={m.id}>
                      {m.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}
          {channelLocked && (
            <span style={{ font: "400 11.5px Outfit, sans-serif", color: "var(--dim)" }}>
              Locked once the order exists
            </span>
          )}
        </div>
      </Band>

      <Band label="Lookup">
        <div className="et-of-lookup">
          <div style={{ flex: 1, minWidth: 0, maxWidth: 320 }}>
            <Form.Item
              name="phone"
              rules={[{ required: true, message: "Please input phone number!" }]}
            >
              <AutoComplete
                options={autocompleteOptions}
                onSearch={handleSearch}
                onSelect={handleSelect}
                notFoundContent={
                  searching ? (
                    // Also inside the portalled panel, so hex rather than var(--dim).
                    <div style={{ textAlign: "center", padding: 8, color: t.dim }}>
                      Searching…
                    </div>
                  ) : undefined
                }
              >
                <Input
                  placeholder="01XXXXXXXXX"
                  className="et-of-mono"
                  prefix={searching ? <Spin size="small" /> : <SearchOutlined style={{ color: "var(--dim)" }} />}
                />
              </AutoComplete>
            </Form.Item>
          </div>
          <div style={{ flex: 1, font: "400 12.5px/1.45 Outfit, sans-serif", color: hintColor, paddingTop: 9 }}>
            {lookupHint}
          </div>
        </div>

        {matched && (
          <MatchStrip
            customer={matched}
            editing={!fieldsDisabled}
            onEdit={() => setFieldsDisabled(false)}
            onReject={rejectMatch}
          />
        )}

        <div className="et-of-idrow">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please input customer name!" }]}
          >
            <Input placeholder="Full name" disabled={fieldsDisabled} />
          </Form.Item>
          {isOffline && (
            <Form.Item
              name="clientOrderNumber"
              label="Client Order / PO #"
            >
              <Input placeholder="e.g. PO-8921" className="et-of-mono" />
            </Form.Item>
          )}
          <Form.Item
            name="facebookId"
            label={isOffline ? "Reference" : "Facebook ID"}
            rules={[{ required: true, message: isOffline ? "Please input a reference!" : "Please input facebook id!" }]}
          >
            <Input placeholder={isOffline ? "Reference no." : "Profile or page"} disabled={fieldsDisabled} />
          </Form.Item>
          <Form.Item
            name="altPhone"
            label="Alt. phone"
            rules={[{ required: !isOffline, message: "Please input alternative phone number!" }]}
          >
            <Input placeholder="01XXXXXXXXX" className="et-of-mono" disabled={fieldsDisabled} />
          </Form.Item>
        </div>
      </Band>

      <Band label="Address" last>
        <div className="et-of-cols3">
          <Form.Item
            name="division"
            label="Division"
            rules={[{ required: true, message: "Please select division!" }]}
          >
            <Select
              placeholder="Select division"
              loading={divisionLoading}
              onSelect={onDivisionSelect}
              disabled={fieldsDisabled}
            >
              {divisions.map((data) => (
                <Option value={data.id} key={data.id}>
                  {data.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="district"
            label="District"
            rules={[{ required: true, message: "Please select district!" }]}
          >
            <Select
              placeholder={districts.length ? "Select district" : "Pick a division first"}
              loading={districtLoading}
              onSelect={onDistrictSelect}
              disabled={fieldsDisabled || !districts.length}
            >
              {districts.map((data) => (
                <Option value={data.id} key={data.id}>
                  {data.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="upazila"
            label="Upazila / Thana"
            rules={[{ required: true, message: "Please select upazila!" }]}
          >
            <Select
              placeholder={upazilas.length ? "Select upazila" : "—"}
              loading={upazilaLoading}
              disabled={fieldsDisabled || !upazilas.length}
            >
              {upazilas.map((data) => (
                <Option value={data.id} key={data.id}>
                  {data.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div className="et-of-cols3__wide">
            <Form.Item
              name="address"
              label="Street address"
              rules={[{ required: true, message: "Please input address!" }]}
            >
              <Input placeholder="House, road, area" disabled={fieldsDisabled} />
            </Form.Item>
          </div>
        </div>
      </Band>
    </SectionCard>
  );
};

export default OrderCustomerForm;
